<template>
  <div v-if="loading || monthlyTraffic?.length" class="w-full flex flex-col rounded-2xl border border-border-normal">
    <!-- Overview -->
    <div class="w-full flex flex-col gap-1.5 p-3 border-b border-border-normal">
      <!-- Header -->
      <div class="w-full flex items-center gap-1">
        <GlobeIcon class="text-text-subdued" />
        <BaseText type="label" size="sm" class="text-text-normal">
          Monthly Traffic
        </BaseText>
      </div>
      <!-- Current Month Traffic -->
      <div v-if="loading" class="skeleton w-20 h-7 rounded-md" />
      <BaseText v-else type="heading" size="lg" class="text-text-muted fade-in">
        {{ currentMonthTraffic.label }}
      </BaseText>
      <!-- Previous Period Change -->
      <div v-if="loading" class="skeleton w-36 h-5 rounded-md" />
      <div v-else class="w-full flex items-center gap-1">
        <PeriodDiffIcon class="transform text-icon-normal" :style="{ transform: trafficChange.value >= 0 ? 'scaleY(1)' : 'scaleY(-1)' }"
        :class="trafficChange.color" />
        <BaseText type="label" size="sm" :class="trafficChange.color">
          {{ trafficChange.label }}
        </BaseText>
        <BaseText type="body" size="sm" class="ml-0.5 text-text-normal">
          since previous month
        </BaseText>
      </div>
    </div>
    <!-- Graph Area -->
    <div class="relative w-full flex items-center justify-center overflow-hidden" style="height: 160px;">
      <BaseLoadingSpinnerCircle v-if="loading" class="text-text-disabled" :width="32" :height="32" :duration="1.2" />
      <div ref="graphArea" class="graph-area" :style="{ left: graphAreaLeftOffset }" />
      <!-- Month Labels -->
      <div class="month-labels">
        <div v-for="label in monthLabels" :key="`month-label-${label}`"
        class="relative w-0 h-full">
          <BaseText type="body" size="xs" class="text-text-normal absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            {{ label }}
          </BaseText>
        </div>
      </div>
    </div>
    <!-- Country Breakdown -->
    <div v-if="loading || countryShares" class="w-full flex items-center gap-3.5 p-3 border-t border-border-normal">
      <!-- Loading Skeleton -->
      <template v-if="loading">
        <div v-for="i in 3" :key="`country-share-skeleton-${i}`" class="skeleton w-14 h-5 rounded" />
      </template>
      <!-- Shares List -->
      <template v-else>
        <BaseTooltipV2 v-for="country in visibleCountryShares" :key="`country-share-${country.code}`"
        :text="country.name" :delay="350">
          <div class="flex items-center gap-1 cursor-default fade-in">
            <div class="p-0.5" style="width: 24px; height: 18px;">
              <img :src="country.flag" :alt="country.name" class="w-full rounded" />
            </div>
            <BaseText type="body" size="sm" class="text-text-muted">
              {{ country.percentage }}
            </BaseText>
          </div>
        </BaseTooltipV2>
        <BaseTooltipV2 v-if="hiddenCountryShares?.length" position="right">
          <div class="p-0.5 rounded border border-border-normal transition-colors duration-100 hover:bg-neutral-50 cursor-default fade-in">
            <BaseText type="label" size="xs" class="px-1 text-text-muted">
              +{{ hiddenCountryShares.length }}
            </BaseText>
          </div>
          <template #tooltip>
            <div class="flex flex-col gap-1 rounded-xl p-1 w-max" style="background-color: rgba(6, 7, 16, 0.96)">
              <div v-for="country in hiddenCountryShares" :key="`country-share-${country.code}`"
              class="flex items-center gap-1 p-1" style="min-width: 160px">
                <div class="p-0.5" style="width: 24px; height: 18px;">
                  <img :src="country.flag" :alt="country.name" class="w-full" style="border-radius: 3px;" />
                </div>
                <BaseText type="body" size="sm" class="text-white">
                  {{ country.name }}
                </BaseText>
                <BaseText type="label" size="sm" class="ml-auto pl-6 text-white">
                  {{ country.percentage }}
                </BaseText>
              </div>
            </div>
          </template>
        </BaseTooltipV2>
      </template>
    </div>
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import formatLabelString from '@/utils/lens/formatGraphLabelString'
import { getCountryName, getCountryFlag } from '@/utils/countryUtils'
import ApexCharts from 'apexcharts'
import moment from 'moment'

// Icons
import GlobeIcon from '@/components/globals/Icons/GlobeIcon.vue'
import PeriodDiffIcon from '@/components/globals/Icons/LensIcons/PeriodDiffIcon.vue'

const GRAPH_COLOR = '#2058A1'
const GRAPH_POINT_X_PADDING = 28 // px

export default {
  name: 'MonthlyTrafficPreview',
  components: {
    GlobeIcon,
    PeriodDiffIcon
  },
  props: {
    pageId: {
      required: true
    }
  },
  data () {
    return {
      monthlyTraffic: null,
      loading: true
    }
  },
  watch: {
    pageId: {
      handler () {
        this.fetchMonthlyTraffic()
      },
      immediate: true
    }
  },
  beforeDestroy () {
    this.chart?.destroy()
    this.chart = null
  },
  computed: {
    currentMonthTraffic () {
      const value = this.monthlyTraffic?.[this.monthlyTraffic.length - 1]?.visits ?? null
      return { value, label: formatLabelString(value, 'number') }
    },
    previousMonthTraffic () {
      const value = this.monthlyTraffic?.[this.monthlyTraffic.length - 2]?.visits ?? null
      return { value, label: formatLabelString(value, 'number') }
    },
    trafficChange () {
      if (!this.currentMonthTraffic.value || !this.previousMonthTraffic.value) return null
      const changePercent = ((this.currentMonthTraffic.value - this.previousMonthTraffic.value) / this.previousMonthTraffic.value) * 100
      return { 
        value: changePercent, 
        label: changePercent.toFixed(1) + '%',
        color: changePercent >= 0 ? 'text-secondary-green-200' : 'text-text-normal'
      }
    },
    monthLabels () {
      if (!this.monthlyTraffic?.length) return []
      return this.monthlyTraffic.slice(1).map(month => moment(month.month).format('MMM'))
    },
    countryShares () {
      // Find the most recent month with country_shares data
      let countryShares = null
      for (let i = this.monthlyTraffic.length - 1; i >= 0; i--) {
        if (this.monthlyTraffic[i]?.country_shares?.length) {
          countryShares = this.monthlyTraffic[i].country_shares
          break
        }
      }
      return countryShares?.map(share => ({
        code: share.CountryCode,
        name: getCountryName(share.CountryCode),
        flag: getCountryFlag(share.CountryCode),
        percentage: formatLabelString(share.Value * 100, 'percent')
      })) ?? null
    },
    visibleCountryShares () {
      return this.countryShares?.slice(0, 3) ?? null
    },
    hiddenCountryShares () {
      return this.countryShares?.slice(3) ?? null
    },
    graphAreaLeftOffset () {
      if (!this.monthlyTraffic?.length) return `78px`
      const segmentCount = this.monthlyTraffic?.length - 2
      return `calc(-1 * ((100% - ${GRAPH_POINT_X_PADDING * 2}px) / ${segmentCount}))`
    }
  },
  methods: {
    async fetchMonthlyTraffic () {
      if (!this.pageId) return
      this.loading = true
      try {
        const trafficData = await ForeplayAPI.Brands.getBrandMonthlyTraffic(
          this.pageId,
          'lookback',
          { lookbackMonths: 5 }
        )
        if (!trafficData?.length) return

        // Trim the values with 0 visits off the edges of the array
        let start = 1 // Skip the first month (renders off screen)
        let end = trafficData.length - 1
        while (start <= end && !trafficData[start].visits) start++
        while (end >= start && !trafficData[end].visits) end--
        this.monthlyTraffic = [trafficData[0], ...trafficData.slice(start, end + 1)]

        this.initializeLineGraph()
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    initializeLineGraph () {
      if (!this.monthlyTraffic?.length) return
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }

      let seriesValues = this.monthlyTraffic.map(month => month.visits)
      const maxValue = Math.max(...seriesValues.slice(1))
      const minValue = Math.min(...seriesValues.slice(1))
      const rangePadding = Math.round(maxValue * 0.05)

      // Clamp the series values to the min and max values
      seriesValues = seriesValues.map(value => Math.max(minValue, Math.min(maxValue, value)))

      const options = {
        series: [{
          name: 'Monthly Traffic',
          data: seriesValues
        }],
        colors: [GRAPH_COLOR],
        chart: {
          type: 'area',
          height: '100%',
          toolbar: { show: false },
          zoom: { enabled: false },
          offsetY: 0,
          offsetX: 0,
          sparkline: { enabled: true },
          animations: { enabled: false }
        },
        stroke: {
          curve: 'straight',
          width: 1,
          lineCap: 'round'
        },
        fill: {
          type: 'gradient',
          gradient: {
            type: 'vertical',
            gradientToColors: [GRAPH_COLOR],
            opacityFrom: 0.4,
            opacityTo: 0.1,
          },
        },
        grid: {
          show: false,
          padding: { left: GRAPH_POINT_X_PADDING, right: GRAPH_POINT_X_PADDING, top: 28, bottom: 44 }
        },
        markers: {
          size: 4,
          colors: ['#FFFFFF'],
          strokeColors: GRAPH_COLOR,
          strokeWidth: 1
        },
        dataLabels: { 
          enabled: true,
          offsetY: -10,
          formatter: (value) => {
            if (!value) return 'N/A'
            return formatLabelString(value, 'number')
          },
          style: {
            fontSize: '12px',
            fontWeight: 500,
            fontFamily: 'Inter',
            colors: ['#2F3547']
          },
          background: { enabled: false },
          dropShadow: { enabled: false },
        },
        tooltip: { enabled: false },
        legend: { enabled: false },
        yaxis: {
          show: false,
          min: minValue - rangePadding,
          max: maxValue + rangePadding * 2,
          forceNiceScale: false
        },
        xaxis: { type: 'numeric', labels: { show: false } }
      }
      this.$nextTick(() => {
        const chartContainer = this.$refs.graphArea
        this.chart = new ApexCharts(chartContainer, options)
        this.chart.render()
      })
    }
  }
}
</script>

<style scoped>
.graph-area {
  position: absolute;
  right: 0px;
  top: 0px;
  bottom: 0px;
}
.month-labels {
  position: absolute;
  bottom: 0px;
  left: 0px;
  right: 0px;
  height: 44px;
  padding-left: 28px;
  padding-right: 28px;
  display: flex;
  justify-content: space-between;
}
.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>